/**
 * Pricing Context
 *
 * Handles all price calculations based on selections.
 * Includes tax calculations with WooCommerce-compatible BTW toggle.
 *
 * @module contexts/pricing
 */

import type { StaffelprijsItem, PricingCalculation, SpoedleveringOptions } from '../../../types';
import type { ProductDataContext } from './product-data.svelte';
import type { SelectionContext } from './selection.svelte';

/**
 * Creates reactive pricing calculations
 *
 * Responsibilities:
 * - Calculate prices based on selections
 * - Find appropriate tier for quantity
 * - Calculate shipping costs based on verzendgroep
 * - Handle tax calculations and display toggle
 *
 * @param productDataContext - The product data context
 * @param selectionContext - The selection context
 * @returns Pricing context with derived values
 */
export function createPricingContext(
	productDataContext: ProductDataContext,
	selectionContext: SelectionContext
) {
	/**
	 * State: Whether to show prices including tax
	 * Defaults to WooCommerce's shop display setting
	 */
	let showInclTax = $state<boolean>(true);

	/**
	 * Initialize showInclTax from WooCommerce settings when config loads
	 */
	$effect(() => {
		const configData = productDataContext.configData;
		if (configData?.product.tax_settings) {
			showInclTax = configData.product.tax_settings.display_shop === 'incl';
		}
	});

	/**
	 * Derived: Get tax rate from product settings
	 */
	const taxRate = $derived.by((): number => {
		const configData = productDataContext.configData;
		return configData?.product.tax_settings?.rate ?? 0.21;
	});
	/**
	 * Derived: Check if current user is an admin
	 */
	const isAdmin = $derived.by((): boolean => {
		const configData = productDataContext.configData;
		return configData?.product.is_admin ?? false;
	});

	/**
	 * State: Admin override for reseller view (null = use actual role)
	 */
	let adminResellerOverride = $state<boolean | null>(null);

	/**
	 * Derived: Check if current user is a reseller (wederverkoper/wederverkoper+)
	 * Admins can override this via the floating toggle
	 */
	const isReseller = $derived.by((): boolean => {
		if (adminResellerOverride !== null) return adminResellerOverride;
		const configData = productDataContext.configData;
		return configData?.product.is_reseller ?? false;
	});

	/**
	 * Derived: Get available quantity tiers for current variation
	 */
	const availableTiers = $derived.by((): StaffelprijsItem[] => {
		const variation = selectionContext.currentVariation;
		if (!variation || !variation.staffelprijzen) return [];

		const tiers = [...variation.staffelprijzen].sort((a, b) => a.aantal - b.aantal);

		// Resellers get the stored reseller price
		if (isReseller) {
			return tiers.map((tier) => ({
				...tier,
				prijs:
					(tier.prijs_reseller && tier.prijs_reseller > 0
						? tier.prijs_reseller
						: tier.prijs)
					+ (tier.toeslag_reseller ?? 0),
			}));
		}

		return tiers.map((tier) => ({
			...tier,
			prijs: tier.prijs + (tier.toeslag_klant ?? 0),
		}));
	});

	/**
	 * Derived: Find the selected tier based on current quantity and druktype
	 */
	const selectedTier = $derived.by((): StaffelprijsItem | null => {
		const quantity = selectionContext.quantity;
		const druktype = selectionContext.selectedDruktype;
		const tiers = availableTiers;

		if (!quantity || !tiers.length) return null;

		// For tier-based selection (not custom), use druktype to disambiguate
		if (!selectionContext.useCustomQuantity && druktype) {
			const exact = tiers.find(t => t.aantal === quantity && t.druktype === druktype);
			if (exact) return exact;
		}

		// Fallback: find the appropriate tier (highest tier that quantity meets)
		const sortedTiers = [...tiers].sort((a, b) => b.aantal - a.aantal);
		return sortedTiers.find((tier) => quantity >= tier.aantal) || sortedTiers[sortedTiers.length - 1];
	});

	/**
	 * Derived: Check if current tier has any spoed options available
	 */
	const isSpoedAvailable = $derived.by((): boolean => {
		const tier = selectedTier;
		if (!tier) return false;
		return !!(tier.spoed_3 || tier.spoed_5 || tier.spoed_8);
	});

	/**
	 * Derived: Check if ANY tier in the current variation has any spoed flag
	 * Used to decide if the Spoed column shows in the OplageEnLevering table
	 */
	const hasSpoedInVariation = $derived.by((): boolean => {
		return availableTiers.some(
			(tier) => tier.spoed_3 || tier.spoed_5 || tier.spoed_8
		);
	});

	/**
	 * Derived: Get spoedlevering options from product settings
	 */
	const spoedleveringOptions = $derived.by((): SpoedleveringOptions | null => {
		const configData = productDataContext.configData;
		return configData?.product.spoedlevering_options ?? null;
	});

	/**
	 * Derived: Calculate surcharge percentage based on selected delivery option
	 */
	const spoedSurchargePercent = $derived.by((): number => {
		const option = selectionContext.deliveryOption;
		const options = spoedleveringOptions;

		if (option === 'normaal' || !options) return 0;

		const keyMap = {
			spoed_3: '3_dagen' as const,
			spoed_5: '5_dagen' as const,
			spoed_8: '8_dagen' as const,
		};

		const settingsKey = keyMap[option];
		if (!settingsKey || !options[settingsKey]?.enabled) return 0;

		return options[settingsKey].surcharge;
	});

	/**
	 * Derived: Calculate unit price from selected tier with optional spoed surcharge
	 * Note: tier.prijs already reflects reseller pricing via availableTiers
	 */
	const unitPrice = $derived.by((): number => {
		const tier = selectedTier;
		const basePrice = tier?.prijs || 0;
		if (spoedSurchargePercent > 0) {
			return basePrice * (1 + spoedSurchargePercent / 100);
		}
		return basePrice;
	});

	/**
	 * Derived: Base unit price without surcharge (for display purposes)
	 */
	const baseUnitPrice = $derived.by((): number => {
		const tier = selectedTier;
		return tier?.prijs || 0;
	});

	/**
	 * Derived: Get effective instelkosten from variation (0 if not set)
	 * Per-tier instelkosten always wins when explicitly set.
	 * For variation-level fallback: digitaal tiers return 0 (setup costs included in price).
	 */
	const effectiveInstelkosten = $derived.by((): number => {
		const variation = selectionContext.currentVariation;
		if (!variation) return 0;
		const tier = selectedTier;
		// Per-tier instelkosten overrides variation-level (regardless of druktype)
		if (tier?.instelkosten != null) {
			return tier.instelkosten;
		}
		// Digitaal tiers: setup costs already included in price
		if (tier?.druktype === 'digitaal') return 0;
		return variation.instelkosten || 0;
	});

	/**
	 * Derived: Get effective toeslag per order (fixed per-order surcharge)
	 */
	const effectiveToeslagOrder = $derived.by((): number => {
		const tier = selectedTier;
		if (!tier) return 0;
		if (isReseller) {
			return tier.toeslag_order_reseller ?? 0;
		}
		return tier.toeslag_order_klant ?? 0;
	});

	/**
	 * Derived: Calculate setup cost per unit
	 */
	const setupCostPerUnit = $derived.by((): number => {
		const quantity = selectionContext.quantity;
		if (!quantity || !effectiveInstelkosten) return 0;
		return effectiveInstelkosten / quantity;
	});

	/**
	 * Round a value up to the nearest 0.05 (matches PHP cart rounding)
	 */
	function roundUpTo005(value: number): number {
		return Math.ceil(Math.round(value * 20 * 10000) / 10000) / 20;
	}

	/**
	 * Derived: Calculate subtotal (unit price * quantity + setup costs)
	 * The line total (unit price * quantity) is rounded up to nearest 0.05
	 * to match the cart/checkout rounding.
	 */
	const subtotal = $derived.by((): number => {
		const quantity = selectionContext.quantity;
		const price = unitPrice;

		if (!quantity) return 0;

		const lineTotal = roundUpTo005(price * quantity);
		return lineTotal + effectiveInstelkosten + effectiveToeslagOrder;
	});

	/**
	 * Derived: Get customer's shipping group (0-5) based on their country
	 */
	const customerShippingGroup = $derived.by((): number => {
		const configData = productDataContext.configData;
		return configData?.product.customer_shipping_group ?? 0;
	});

	/**
	 * Derived: Calculate shipping cost from selected tier's verzendkosten
	 */
	const shipping = $derived.by((): number => {
		const tier = selectedTier;
		if (!tier) return 0;

		const key = `verzendkosten_${customerShippingGroup}` as keyof typeof tier;
		return Number(tier[key]) || 0;
	});

	/**
	 * Derived: Calculate total price (subtotal + shipping) - excl. tax
	 */
	const total = $derived.by((): number => {
		return subtotal + shipping;
	});

	/**
	 * Derived: Calculate total price including tax
	 */
	const totalInclTax = $derived.by((): number => {
		return total * (1 + taxRate);
	});

	/**
	 * Derived: Calculate subtotal including tax
	 */
	const subtotalInclTax = $derived.by((): number => {
		return subtotal * (1 + taxRate);
	});

	/**
	 * Derived: Calculate shipping including tax
	 */
	const shippingInclTax = $derived.by((): number => {
		return shipping * (1 + taxRate);
	});

	/**
	 * Derived: Calculate unit price including tax
	 */
	const unitPriceInclTax = $derived.by((): number => {
		return unitPrice * (1 + taxRate);
	});

	/**
	 * Toggle tax display mode
	 */
	function toggleTaxDisplay(inclTax: boolean) {
		showInclTax = inclTax;
	}

	/**
	 * Toggle reseller view (admin only)
	 */
	function toggleResellerView(reseller: boolean) {
		adminResellerOverride = reseller;
	}

	/**
	 * Derived: Get complete pricing calculation object
	 */
	const pricing = $derived.by((): PricingCalculation => {
		return {
			unitPrice: unitPrice,
			setupCostPerUnit: setupCostPerUnit,
			subtotal: subtotal,
			shipping: shipping,
			total: total,
			tier: selectedTier,
			effectiveToeslagOrder: effectiveToeslagOrder,
		};
	});

	/**
	 * Helper: Add working days to a date (excluding weekends)
	 *
	 * @param startDate - The starting date
	 * @param workingDays - Number of working days to add
	 * @returns The resulting date
	 */
	function addWorkingDays(startDate: Date, workingDays: number): Date {
		const result = new Date(startDate);
		let daysAdded = 0;

		while (daysAdded < workingDays) {
			result.setDate(result.getDate() + 1);
			// Check if it's a weekday (0 = Sunday, 6 = Saturday)
			if (result.getDay() !== 0 && result.getDay() !== 6) {
				daysAdded++;
			}
		}

		return result;
	}

	/**
	 * Derived: Get delivery days based on selected option
	 */
	const deliveryDays = $derived.by((): number => {
		const configData = productDataContext.configData;
		if (!configData) return 5;

		const option = selectionContext.deliveryOption;
		if (option === 'normaal') {
			return selectedTier?.levertijd_dagen || configData.product.levertijd_dagen;
		}

		const options = spoedleveringOptions;
		if (!options) return configData.product.levertijd_dagen;

		const keyMap = {
			spoed_3: '3_dagen' as const,
			spoed_5: '5_dagen' as const,
			spoed_8: '8_dagen' as const,
		};

		const settingsKey = keyMap[option];
		if (settingsKey && options[settingsKey]) {
			return options[settingsKey].days;
		}

		return configData.product.levertijd_dagen;
	});

	/**
	 * Derived: Calculate expected delivery date (working days only)
	 */
	const deliveryDate = $derived.by(() => {
		const configData = productDataContext.configData;
		if (!configData) return '';

		const workingDays = deliveryDays;
		const today = new Date();
		const deliveryDateObj = addWorkingDays(today, workingDays);

		// Format as dd-mm-yyyy for all languages
		const day = String(deliveryDateObj.getDate()).padStart(2, '0');
		const month = String(deliveryDateObj.getMonth() + 1).padStart(2, '0');
		const year = deliveryDateObj.getFullYear();

		return `${day}-${month}-${year}`;
	});

	return {
		// Derived values (use getters to access $derived values)
		get availableTiers() {
			return availableTiers;
		},
		get selectedTier() {
			return selectedTier;
		},
		get unitPrice() {
			return unitPrice;
		},
		get baseUnitPrice() {
			return baseUnitPrice;
		},
		get isReseller() {
			return isReseller;
		},
		get isAdmin() {
			return isAdmin;
		},
		get isSpoedAvailable() {
			return isSpoedAvailable;
		},
		get hasSpoedInVariation() {
			return hasSpoedInVariation;
		},
		get spoedSurchargePercent() {
			return spoedSurchargePercent;
		},
		get spoedleveringOptions() {
			return spoedleveringOptions;
		},
		get deliveryDays() {
			return deliveryDays;
		},
		get setupCostPerUnit() {
			return setupCostPerUnit;
		},
		get subtotal() {
			return subtotal;
		},
		get shipping() {
			return shipping;
		},
		get total() {
			return total;
		},
		get pricing() {
			return pricing;
		},
		get deliveryDate() {
			return deliveryDate;
		},
		// Tax-related values
		get taxRate() {
			return taxRate;
		},
		get showInclTax() {
			return showInclTax;
		},
		get totalInclTax() {
			return totalInclTax;
		},
		get subtotalInclTax() {
			return subtotalInclTax;
		},
		get shippingInclTax() {
			return shippingInclTax;
		},
		get unitPriceInclTax() {
			return unitPriceInclTax;
		},
		get effectiveToeslagOrder() {
			return effectiveToeslagOrder;
		},
		// Methods
		toggleTaxDisplay,
		toggleResellerView,
		addWorkingDays,
	};
}

/**
 * Type for pricing context
 */
export type PricingContext = ReturnType<typeof createPricingContext>;
